<script setup lang="ts">
import { computed } from 'vue'
import type { KeypointGroup } from '@/composables/useKeypointSearch'
import HitCard from './HitCard.vue'

const props = defineProps<{
  group: KeypointGroup
  sortedBy?: 'relevance' | 'quality'
  stanceColor?: 'pro' | 'con'
  loadingMore?: boolean
  greyed?: boolean
  expanded?: boolean
  flatInfo?: boolean
}>()

const emit = defineEmits<{
  (e: 'load-more', keyPoint: string, stance: 'pro' | 'con' | undefined): void
  (e: 'update:expanded', value: boolean): void
}>()

const hasMore = computed(() =>
  props.group.arguments.length < props.group.argument_count
)

const noKeyPoint = computed(() => !!props.group.noKeyPoint || props.group.key_point === '')

// Border and badge classes based on state
const borderClass = computed(() => {
  if (props.greyed) return 'border-gray-200'
  if (noKeyPoint.value) return 'border-gray-300'
  return props.stanceColor === 'con' ? 'border-[#3A91A5]' : 'border-purple-600'
})

const bgClass = computed(() => {
  if (props.greyed) return 'bg-gray-50'
  return 'bg-white'
})

const labelClass = computed(() => {
  if (props.greyed) return 'text-gray-400'
  if (noKeyPoint.value) return 'text-gray-500'
  return 'text-gray-900'
})

const badgeClass = computed(() => {
  if (props.greyed) return 'bg-gray-100 text-gray-400'
  if (noKeyPoint.value) return 'bg-gray-100 text-gray-500'
  return props.stanceColor === 'con'
    ? 'bg-[#3A91A5]/10 text-[#3A91A5]'
    : 'bg-purple-50 text-purple-700'
})

const isClickable = computed(() => !props.greyed)

const displayKeyPoint = computed(() => {
  const kp = props.group.key_point
  if (!kp) return kp
  return kp.charAt(0).toUpperCase() + kp.slice(1)
})
</script>

<template>
  <div
    class="rounded-2xl border shadow-sm overflow-hidden"
    :class="[bgClass, borderClass]"
  >
    <!-- Keypoint header -->
    <button
      @click="isClickable && emit('update:expanded', !expanded)"
      class="w-full flex items-center justify-between gap-3 px-4 py-3 text-left transition-colors"
      :class="isClickable ? 'hover:bg-gray-50' : 'cursor-default'"
    >
      <div class="flex items-center gap-2 min-w-0">
        <!-- Expand/collapse triangle -->
        <svg
          v-if="isClickable"
          class="w-4 h-4 shrink-0 text-gray-400 transition-transform"
          :class="{ 'rotate-90': expanded }"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path d="M7.5 5.25L12.25 10L7.5 14.75V5.25Z" />
        </svg>

        <span class="text-sm font-medium" :class="labelClass">
          {{ noKeyPoint ? 'Arguments without key point' : displayKeyPoint }}
        </span>
      </div>

      <span class="shrink-0 text-xs rounded-full px-2 py-0.5" :class="badgeClass">
        {{ group.argument_count }} argument{{ group.argument_count === 1 ? '' : 's' }}
      </span>
    </button>

    <!-- Expanded arguments -->
    <div v-if="expanded && isClickable" class="border-t border-gray-100 px-4 py-3 space-y-3 bg-gray-50/50">
      <p v-if="group.arguments.length === 0" class="text-sm text-gray-400 italic">No matching arguments loaded yet.</p>
      <HitCard
        v-for="arg in group.arguments"
        :key="arg.id"
        :hit="arg"
        :sorted-by="sortedBy"
        :flat-info="flatInfo"
        hide-key-point
      />

      <!-- Load more per keypoint -->
      <div v-if="hasMore" class="flex justify-center pt-1">
        <button
          @click.stop="emit('load-more', group.key_point, group.stance)"
          :disabled="loadingMore"
          class="rounded-xl border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {{ loadingMore ? 'Loading…' : `Load more (${group.arguments.length}/${group.argument_count})` }}
        </button>
      </div>
    </div>
  </div>
</template>
